import React, { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ButtonNew } from "components/buttonNew";
import "./emailBuilderDesignErrorBoundary.scss";

interface IEmailBuilderDesignErrorBoundaryProps {
  children: ReactNode;
  resetKey: number | string;
  canUndo: boolean;
  onUndo: () => void;
}

interface IEmailBuilderDesignErrorBoundaryState {
  hasError: boolean;
}

export class EmailBuilderDesignErrorBoundary extends React.Component<
  IEmailBuilderDesignErrorBoundaryProps,
  IEmailBuilderDesignErrorBoundaryState
> {
  state: IEmailBuilderDesignErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): Partial<IEmailBuilderDesignErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    if (process.env.NODE_ENV !== "production") {
      console.error("Email builder design surface error", error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: IEmailBuilderDesignErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  private handleUndo = (): void => {
    this.props.onUndo();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <EmailBuilderDesignErrorFallback
        canUndo={this.props.canUndo}
        onRetry={this.handleRetry}
        onUndo={this.handleUndo}
      />
    );
  }
}

interface IEmailBuilderDesignErrorFallbackProps {
  canUndo: boolean;
  onRetry: () => void;
  onUndo: () => void;
}

function EmailBuilderDesignErrorFallback(props: IEmailBuilderDesignErrorFallbackProps) {
  const { t } = useTranslation();
  const { canUndo, onRetry, onUndo } = props;
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <div className="email-builder-design-error" role="alert">
      <div className="email-builder-design-error__card">
        <h2
          ref={titleRef}
          tabIndex={-1}
          className="email-builder-design-error__title"
        >
          {t("Something went wrong in the editor")}
        </h2>
        <p className="email-builder-design-error__description">
          {t(
            "A recent change could not be shown on the canvas. Undo to restore the last good state, or try again.",
          )}
        </p>
        <div className="email-builder-design-error__actions">
          {canUndo ? (
            <ButtonNew type="primary" onClick={onUndo}>
              {t("Undo")}
            </ButtonNew>
          ) : null}
          <ButtonNew type={canUndo ? "secondary" : "primary"} onClick={onRetry}>
            {t("Try again")}
          </ButtonNew>
        </div>
      </div>
    </div>
  );
}
